import type { QueryClient } from "@tanstack/react-query";

import { ChatResponseStatus } from "@/components/ai-chat/types";

import type { ChatHistoryEntry, ChatThreadSummary } from "@/types/chat-thread";

import {
  askDecodaThreadHistoryQueryKey,
  askDecodaThreadsQueryKey,
} from "@/api/ai/chat-threads";

const DEFAULT_MODEL_NAME = "";

export type ChatHistoryUpdater = (
  previous: ChatHistoryEntry[],
) => ChatHistoryEntry[];

export function updateAskDecodaHistoryCache(
  queryClient: QueryClient,
  tenantSubDomain: string | undefined,
  threadId: string,
  updater: ChatHistoryUpdater,
): void {
  queryClient.setQueryData<ChatHistoryEntry[]>(
    askDecodaThreadHistoryQueryKey(tenantSubDomain, threadId),
    (current = []) => updater(current),
  );
}

export function appendOptimisticHistoryEntry(params: {
  queryClient: QueryClient;
  tenantSubDomain: string | undefined;
  threadId: string;
  id: string;
  requestId: string;
  question: string;
  createdDate: Date;
  files?: ChatHistoryEntry["files"];
}): void {
  const { queryClient, tenantSubDomain, threadId, id, requestId, question } =
    params;
  updateAskDecodaHistoryCache(
    queryClient,
    tenantSubDomain,
    threadId,
    (current) => [
      ...current,
      {
        id,
        requestId,
        requestAccepted: false,
        threadId,
        question,
        answer: "",
        responseBlocks: [],
        status: ChatResponseStatus.STREAMING,
        userId: "",
        feedbackType: null,
        feedbackText: null,
        modelName: DEFAULT_MODEL_NAME,
        createdDate: params.createdDate.toISOString(),
        files: params.files,
        artifacts: [],
        executions: [],
        sources: null,
      },
    ],
  );
}

function matchesEntry(
  entry: ChatHistoryEntry,
  identity: { id?: string; requestId?: string },
): boolean {
  return Boolean(
    (identity.id && entry.id === identity.id) ||
    (identity.requestId && entry.requestId === identity.requestId),
  );
}

export function patchHistoryEntry(
  queryClient: QueryClient,
  tenantSubDomain: string | undefined,
  threadId: string,
  identity: { id?: string; requestId?: string },
  patcher: (entry: ChatHistoryEntry) => ChatHistoryEntry,
): void {
  updateAskDecodaHistoryCache(
    queryClient,
    tenantSubDomain,
    threadId,
    (current) =>
      current.map((entry) =>
        matchesEntry(entry, identity) ? patcher(entry) : entry,
      ),
  );
}

/** A committed projection replaces every server-owned field, regardless of its
 * transport or lifecycle. Only attachments and in-flight feedback have separate
 * optimistic ownership. An absent requestId ends the old subscription identity.
 * Freshness/admission checks belong to callers, never domain-field merging.
 */
function committedHistoryEntry(
  snapshot: ChatHistoryEntry,
  previous?: ChatHistoryEntry,
): ChatHistoryEntry {
  return {
    ...snapshot,
    files: snapshot.files ?? previous?.files,
    // Feedback/read annotations are product metadata, never checkpoint fields.
    feedbackType: null,
    feedbackText: null,
    pendingFeedback: undefined,
    requestId: snapshot.requestId,
    requestAccepted: true,
  };
}

export function replaceCommittedHistoryEntry(
  current: ChatHistoryEntry[],
  snapshot: ChatHistoryEntry,
): ChatHistoryEntry[] {
  const previous = current.find((entry) => entry.id === snapshot.id);
  const replacement = committedHistoryEntry(snapshot, previous);
  return previous
    ? current.map((entry) => (entry.id === snapshot.id ? replacement : entry))
    : [...current, replacement];
}

export function mergeFetchedHistory(
  current: ChatHistoryEntry[] = [],
  fetched: ChatHistoryEntry[],
): ChatHistoryEntry[] {
  const fetchedById = new Map(fetched.map((entry) => [entry.id, entry]));
  const seen = new Set<string>();
  const merged = current.map((entry) => {
    const fetchedEntry = fetchedById.get(entry.id);
    seen.add(entry.id);
    if (!fetchedEntry) return entry;
    return committedHistoryEntry(fetchedEntry, entry);
  });
  for (const entry of fetched) {
    if (!seen.has(entry.id)) merged.push(committedHistoryEntry(entry));
  }
  return merged;
}

export function upsertThreadSummary(
  queryClient: QueryClient,
  tenantSubDomain: string | undefined,
  summary: ChatThreadSummary,
): void {
  queryClient.setQueryData<ChatThreadSummary[]>(
    askDecodaThreadsQueryKey(tenantSubDomain),
    (current = []) => [
      summary,
      ...current.filter((item) => item.threadId !== summary.threadId),
    ],
  );
}
